import React from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DatatableRow from './DatatableRow';
import { faCaretUp, faCaretDown, faTimes } from "@fortawesome/free-solid-svg-icons";

const DatatableTable = ({
  tableContainerRef,
  cols,
  colWidths,
  setColWidths,
  rows,
  loadingState,
  effectivePerPage,
  checkboxColumn,
  searchColumnsEnabled,
  nameColumnsUppercase,
  adjustColumnSize,
  rowIdField,
  onRowClick,
  onRowDoubleClick,
  draggingKey,
  dragOverKey,
  dragOverColIdx,
  selectedRows,
  toggleSelectAll,
  toggleSelectRow,
  sortBy,
  sortDir,
  handleSortClick,
  columnFilters,
  handleColumnFilterChange,
  inputHoverColIdx,
  setInputHoverColIdx,
  orderColumns,
  handleDragStart,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleDragDropReset
}) => {
  const visibleCols = cols.map((col, idx) => ({ ...col, originalIdx: idx })).filter(col => col.isVisible !== false);

  return (
    <div ref={tableContainerRef} className={`datatable-table-container table-responsive border rounded shadow-sm datatable-scrollbar ${rows.length === 0 && !loadingState ? 'datatable-empty' : ''}`}>
      <table
        className="table table-sm table-hover mb-0"
        style={{
          tableLayout: adjustColumnSize ? 'fixed' : 'auto',
          width: adjustColumnSize ? 'max-content' : '100%',
          minWidth: '100%'
        }}
      >
        <thead className="table-light sticky-top" style={{ top: 0, zIndex: 5 }}>
          {/* Fila de nombres de columnas */}
          <tr className="datatable-th-container">
            {checkboxColumn && (
              <th rowSpan={searchColumnsEnabled ? 2 : 1} className="sticky-top datatable-th-checkbox">
                <div className="d-flex align-items-center justify-content-center" style={{ height: '100%' }}>
                  <input 
                    type="checkbox" 
                    onChange={toggleSelectAll} 
                    checked={rows.length > 0 && selectedRows.size === rows.length}
                    style={{ cursor: 'pointer', width: 16, height: 16 }}
                  />
                </div>
              </th>
            )}
            {visibleCols.map((col, loopIdx) => {
              const isSorted = sortBy === col.key;
              const dir = isSorted ? sortDir : null;
              const headerAlign = col.headerAlign || 'left';
              const label = nameColumnsUppercase ? String(col.label).toUpperCase() : col.label;

              const currentWidth = colWidths[col.originalIdx]
                ? colWidths[col.originalIdx]
                : (col.minWidth ? col.minWidth : 'auto');

              let dragClass = "";
              if (draggingKey === col.key) {
                dragClass = "dragging";
              } else if (dragOverKey === col.key && draggingKey) {
                dragClass = "drag-target";
              }

              const thStyle = {
                textAlign: headerAlign,
                width: currentWidth,
                minWidth: col.minWidth || (adjustColumnSize ? '50px' : 'auto'),
                position: "relative",
                borderRight: loopIdx < visibleCols.length - 1 ? "1px solid #dee2e6" : undefined,
                boxSizing: 'border-box',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                transition: 'background 0.2s, box-shadow 0.2s, border 0.2s',
              };

              const handleResizerMouseDown = (e) => {
                if (!adjustColumnSize) return;
                e.preventDefault();
                e.stopPropagation();
                const headerCell = e.target.closest('th');
                if (!headerCell) return;
                const startX = e.clientX;
                const startWidth = headerCell.getBoundingClientRect().width;
                const onMouseMove = (ev) => {
                  requestAnimationFrame(() => {
                    const newWidth = Math.max(50, startWidth + (ev.clientX - startX));
                    setColWidths(prev => {
                      const updated = [...prev];
                      updated[col.originalIdx] = newWidth + "px";
                      return updated;
                    });
                  });
                };
                const onMouseUp = () => {
                  document.removeEventListener("mousemove", onMouseMove);
                  document.removeEventListener("mouseup", onMouseUp);
                };
                document.addEventListener("mousemove", onMouseMove);
                document.addEventListener("mouseup", onMouseUp);
              };

              return (
                <th
                  key={col.key}
                  scope="col"
                  style={thStyle}
                  className={`datatable-table-th ${dragClass}`}
                  draggable={orderColumns}
                  onDragStart={(e) => {
                    if (e.target.classList.contains('th-resize-handle')) {
                      e.preventDefault();
                      return false;
                    }
                    if (e.target.tagName === 'INPUT') {
                      e.preventDefault();
                      return false;
                    }
                    handleDragStart(e, col.key);
                  }}
                  onDragOver={(e) => handleDragOver(e, col.key, col.originalIdx)}
                  onDragLeave={(e) => handleDragLeave(e, col.key)}
                  onDrop={(e) => handleDrop(e, col.key)}
                  onDragEnd={() => {
                    handleDragDropReset && handleDragDropReset();
                  }}
                >
                  <div className="d-flex align-items-center gap-1 justify-content-between">
                    <span 
                      className="small fw-semibold text-truncate flex-grow-1" 
                      style={{ userSelect: 'none', cursor: col.sortable ? 'pointer' : 'default' }}
                      title={col.sortable ? "Clic para ordenar" : label}
                      onClick={() => {
                        if (col.sortable) {
                          if (!isSorted) handleSortClick(col.key, 'asc');
                          else if (dir === 'asc') handleSortClick(col.key, 'desc');
                          else handleSortClick('', '');
                        }
                      }}
                    >
                      {label}
                    </span>
                    {col.sortable && (
                      <div className="th-sort d-flex align-items-center" style={{ marginLeft: 4 }}>
                        <div style={{ position: 'relative', width: '12px', height: '18px' }}>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleSortClick(col.key, isSorted && dir === 'asc' ? '' : 'asc'); }}
                            className={isSorted && dir === 'asc' ? "datatable-th-sort-btn-active" : "datatable-th-sort-btn-inactive"}
                            style={{ position: 'absolute', top: '0px', left: 0, padding: 0, border: 'none', background: 'transparent', width: '12px', height: '9px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            title="Ordenar ascendente"
                          >
                            <FontAwesomeIcon icon={faCaretUp} style={{ fontSize: '14px', marginTop: '3px' }} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleSortClick(col.key, isSorted && dir === 'desc' ? '' : 'desc'); }}
                            className={isSorted && dir === 'desc' ? "datatable-th-sort-btn-active" : "datatable-th-sort-btn-inactive"}
                            style={{ position: 'absolute', bottom: '0px', left: 0, padding: 0, border: 'none', background: 'transparent', width: '12px', height: '9px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            title="Ordenar descendente"
                          >
                            <FontAwesomeIcon icon={faCaretDown} style={{ fontSize: '14px', marginBottom: '3px' }} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  {adjustColumnSize && (
                    <div
                      className="th-resize-handle"
                      onMouseDown={handleResizerMouseDown}
                      onClick={e => e.stopPropagation()}
                      title="Arrastra para cambiar ancho"
                    />
                  )}
                </th>
              );
            })}
          </tr>
          {/* Fila de buscadores por columna */}
          {searchColumnsEnabled && (
            <tr className="datatable-filter-row">
              {visibleCols.map((col, loopIdx) => (
                (() => {
                  let dragClass = "";
                  if (draggingKey === col.key) {
                    dragClass = "dragging";
                  } else if (dragOverKey === col.key && draggingKey) {
                    dragClass = "drag-target";
                  }
                  const currentWidth = colWidths[col.originalIdx]
                    ? colWidths[col.originalIdx]
                    : (col.minWidth ? col.minWidth : 'auto');

                  const thStyle = {
                    padding: '2px',
                    width: currentWidth,
                    minWidth: col.minWidth || (adjustColumnSize ? '50px' : 'auto'),
                    borderRight: loopIdx < visibleCols.length - 1 ? "1px solid #dee2e6" : undefined,
                    boxSizing: 'border-box',
                  };
                  return (
                    <th
                      key={col.key}
                      style={thStyle}
                      className={`datatable-table-th ${dragClass}`}
                      draggable={orderColumns && inputHoverColIdx !== col.originalIdx}
                      onDragStart={(e) => {
                        if (e.target.classList.contains('th-resize-handle') || e.target.tagName === 'INPUT') {
                          e.preventDefault();
                          return false;
                        }
                        handleDragStart(e, col.key);
                      }}
                      onDragOver={(e) => handleDragOver(e, col.key, col.originalIdx)}
                      onDragLeave={(e) => handleDragLeave(e, col.key)}
                      onDrop={(e) => handleDrop(e, col.key)}
                      onDragEnd={() => {
                        handleDragDropReset && handleDragDropReset();
                      }}
                    >
                      <div className="mt-0 position-relative">
                        <input
                          type="text"
                          value={columnFilters[col.key] || ""}
                          onChange={(e) => col.searchable ? handleColumnFilterChange(col.key, e.target.value) : undefined}
                          className={`form-control datatable-filter-input`}
                          placeholder={col.searchable ? `Filtrar por ` : "------"}
                          disabled={!col.searchable}
                          draggable={false}
                          onDragStart={e => e.preventDefault()}
                          onMouseEnter={() => setInputHoverColIdx(col.originalIdx)}
                          onMouseLeave={() => setInputHoverColIdx(null)}
                          style={{ paddingRight: columnFilters[col.key] ? '24px' : '8px' }}
                        />
                        {col.searchable && columnFilters[col.key] && (
                          <button
                            type="button"
                            className="btn btn-sm position-absolute"
                            style={{ right: '4px', top: '50%', transform: 'translateY(-50%)', padding: '0 4px', color: '#888', border: 'none', background: 'transparent' }}
                            onClick={() => handleColumnFilterChange(col.key, '')}
                            title="Limpiar filtro"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        )}
                      </div>
                    </th>
                  );
                })()
              ))}
            </tr>
          )}
        </thead>
        <tbody>
          {loadingState ? (
            Array.from({ length: effectivePerPage }).map((_, rowIdx) => (
              <tr key={`skeleton-row-${rowIdx}`}>
                {checkboxColumn && (
                  <td style={{ width: 40 }}><input type="checkbox" disabled /></td>
                )}
                {visibleCols.map((col, colIdx) => {
                  const dataAlign = col.dataAlign || 'left';
                  const isDragging = draggingKey === col.key;
                  
                  const cellStyle = {
                    textAlign: dataAlign,
                    width: colWidths[col.originalIdx] || undefined,
                    maxWidth: colWidths[col.originalIdx] || undefined,
                    minWidth: col.minWidth || undefined,
                    borderRight: colIdx < visibleCols.length - 1 ? "1px solid #14406e22" : undefined,
                    color: '#222', fontSize: 13, padding: '8px 8px', boxSizing: 'border-box',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    transition: 'background 0.2s, box-shadow 0.2s, border 0.2s',
                    opacity: isDragging ? 0.4 : 1,
                    background: isDragging ? 'rgba(42, 119, 185, 0.05)' : undefined
                  };
                  return (
                    <td key={`skeleton-cell-${col.key}-${rowIdx}`} style={cellStyle}>
                      <div className="skeleton-box wave" style={{ height: 18, width: '100%', borderRadius: 6, margin: 0 }} />
                    </td>
                  );
                })}
              </tr>
            ))
          ) : (
            rows.length === 0 ? (
              <tr>
                <td colSpan={visibleCols.length + (checkboxColumn ? 1 : 0)} className="py-4 datatable-empty-row" style={{ position: 'relative' }}>
                  <div style={{ position: 'sticky', left: '50%', transform: 'translateX(-50%)', display: 'inline-block', fontWeight: 500 }}>
                    No se encontraron registros.
                  </div>
                </td>
              </tr>
            ) : (
              rows.slice(0, effectivePerPage).map((row, idx) => (
                <DatatableRow
                  key={row[rowIdField] ?? `${rowIdField}-${idx}`}
                  row={row}
                  idx={idx}
                  cols={visibleCols}
                  colWidths={colWidths}
                  checkboxColumn={checkboxColumn}
                  adjustColumnSize={adjustColumnSize}
                  rowIdField={rowIdField}
                  onRowClick={onRowClick}
                  onRowDoubleClick={onRowDoubleClick}
                  draggingKey={draggingKey}
                  dragOverColIdx={dragOverColIdx}
                  selectedRows={selectedRows}
                  toggleSelectRow={toggleSelectRow}
                />
              ))
            )
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DatatableTable;
